import { createElement, useEffect, useState } from "react";
import { Trans } from "react-i18next";
import { useValidator } from "@common/hooks/validation";
import { LegalEntitySaveModel } from "./AddLegalEntityDialog.types";
import { useDotLegalSnackbar } from "@dotlegal/dotlegal-ui-components";
import { useTranslation } from "@localization/useTranslation";
import { post, setJwtToken } from "@common/api/apiShared";
import { IAddLegalEntityDialog } from "./AddLegalEntityDialog";
import { useUrlProvider } from "../../useUrlProvider";
import { useNavigate } from "react-router";
import { StandardLegalEntityType } from "../LegalEntity.types";
import { useLegalEntityTypeNames } from "../useLegalEntityTypeNames";
import { LegalEntityScope, toLegalEntityScope } from "../legalEntities/LegalEntities.types";
import { CvrResultViewModel } from "../cvrSelector/CvrSelector.types";
import { useDotLegalMutation } from "@common/hooks/dotLegalReactQuery";
import { useSelectableEntityTemplatesForCreation } from "@common/hooks/useSelectableItemQueries";
import { LegalEntityStandardField, TemplateEntityType } from "../../entityTemplates/EntityTemplate.types";
import { toEntityTemplateField } from "../../entityTemplates/requiredAtCreation";
import { hasFieldData } from "../../entityTemplates/fieldValue";
import { LegalEntityFieldModel } from "../../entityTemplates/LegalEntityFieldRenderers";
import { getFieldName, getLegalEntityFieldValue } from "../editLegalEntity/EditLegalEntityValidator.hooks";
import { ValidationError } from "@common/validationError";
import { usePlanContext } from "../../auth/planProvider/PlanProvider";
import { useUserContext } from "../../auth/userContextProvider/UserContextProvider";

// The dialog asks for name and country itself, so promoting them in a template must not repeat them here.
const skippedFields: Set<number> = new Set([LegalEntityStandardField.LegalEntityName, LegalEntityStandardField.LegalEntityCountry]);

function createEmptyRequiredFieldValues(isApproved: boolean): LegalEntityFieldModel {
    return {
        id: "",
        types: [],
        isApproved: isApproved,
        isActive: true,
        isDataProcessorOnTransfers: false,
        isSubDataProcessorOnTransfers: false,
        businessAreaIds: [],
        complianceAreaIds: [],
        certificationIds: [],
        subContractorIds: [],
        shouldAutoAddSubContractors: false,
        customFieldValues: [],
    };
}

function getCvrFieldValues(cvrResult: CvrResultViewModel | undefined): Partial<LegalEntityFieldModel> {
    return {
        address: cvrResult?.address,
        city: cvrResult?.city,
        zipCode: cvrResult?.zipCode,
        companyRegistrationNumber: cvrResult?.companyRegistrationNumber,
        website: cvrResult?.website,
        description: cvrResult?.description,
    };
}

export function useAddLegalEntityDialogHook(props: IAddLegalEntityDialog) {
    const snackbar = useDotLegalSnackbar();
    const { translateString } = useTranslation();
    const { getLegalEntityEditUrl } = useUrlProvider();
    const navigate = useNavigate();
    const { platformFeatures } = usePlanContext();
    const { permissions } = useUserContext();

    // Without a scope the dialog was opened from inside another flow, which keeps the user where they are.
    const isInlineCreation = props.legalEntityScope === undefined;
    const isGroupEntityCreation = props.legalEntityScope === LegalEntityScope.GroupEntity;
    const isExternalCreation = props.legalEntityScope === LegalEntityScope.External;
    // The group entity list is gated on its own permission, so creating there is a full creation for a user
    // who may manage group entities but not companies.
    const canCreateCompany = isGroupEntityCreation ? permissions.canManageGroupEntities : permissions.companyPermissions.create;

    const { getStandardTypeId } = useLegalEntityTypeNames();

    const [saveModel, setSaveModel] = useState<LegalEntitySaveModel>(getDefaultSaveModel());
    const [selectedCvrResult, setSelectedCvrResult] = useState<CvrResultViewModel | undefined>(undefined);
    const [editedLegalEntity, setEditedLegalEntity] = useState<LegalEntityFieldModel>(createEmptyRequiredFieldValues(canCreateCompany));
    const [selectedTagIds, setSelectedTagIds] = useState<Array<string>>([]);
    const [showRequiredFieldErrors, setShowRequiredFieldErrors] = useState(false);

    // The name and country the dialog asks for itself are the only fields the CVR lookup disables.
    const disableFields = selectedCvrResult !== undefined;

    const { data: entityTemplatesData, isLoading: templatesIsLoading } = useSelectableEntityTemplatesForCreation(TemplateEntityType.LegalEntity, false);

    const templates = entityTemplatesData?.map((x) => ({ id: x.id, name: x.name })) ?? [];

    const selectedTemplate = entityTemplatesData?.find((t) => t.id === saveModel.entityTemplateId);
    const selectedTemplateMissingAccess = selectedTemplate?.missingRequiredAtCreationFieldAccess ?? false;

    // A user who may only create inline never approves the entity, so promoting the field would render a
    // checkbox they cannot act on - and one hasFieldData reads as empty, which would block creation entirely.
    const isFieldCollectedAtCreation = (standardField?: number | null) =>
        standardField == null || (!skippedFields.has(standardField) && (canCreateCompany || standardField !== LegalEntityStandardField.LegalEntityApproved));

    const requiredAtCreationTemplateFields = (selectedTemplate?.requiredAtCreationFields ?? [])
        .filter((f) => isFieldCollectedAtCreation(f.standardFieldIdentifier))
        .map(toEntityTemplateField);

    const tagsRequiredAtCreation =
        platformFeatures.tags &&
        permissions.canAccessTags &&
        requiredAtCreationTemplateFields.some((f) => f.standardFieldIdentifier === LegalEntityStandardField.LegalEntityTags);

    const subContractorsRequiredAtCreation = requiredAtCreationTemplateFields.some(
        (f) => f.standardFieldIdentifier === LegalEntityStandardField.LegalEntitySubContractors
    );

    const standardRequiredFields = requiredAtCreationTemplateFields
        .filter(
            (f) =>
                f.standardFieldIdentifier &&
                f.standardFieldIdentifier !== LegalEntityStandardField.LegalEntityTags &&
                // Sub contractors persist against an entity id in the normal renderer, so creation collects
                // them with a plain selector instead.
                f.standardFieldIdentifier !== LegalEntityStandardField.LegalEntitySubContractors
        )
        .sort((a, b) => a.order - b.order);

    const customRequiredFields = requiredAtCreationTemplateFields.filter((f) => f.customInputFieldId).sort((a, b) => a.order - b.order);

    const hasStandardRequiredFields = standardRequiredFields.length > 0 || tagsRequiredAtCreation || subContractorsRequiredAtCreation;

    const validation = useValidator<LegalEntitySaveModel>(
        (validators) => ({
            name: validators.validateNotEmpty((item) => item.name, translateString("name")),
            countryCode: validators.validateNotEmpty((item) => item.countryCode, translateString("country")),
            entityTemplateId: validators.validateNotEmpty((item) => item.entityTemplateId, translateString("legalEntityTemplate")),
        }),
        [saveModel?.countryCode, saveModel?.name, saveModel?.entityTemplateId]
    );

    validation.check(saveModel);

    useEffect(() => {
        if (entityTemplatesData?.length === 1 && !saveModel.entityTemplateId) {
            onTemplateChange(entityTemplatesData[0].id);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [entityTemplatesData, saveModel.entityTemplateId]);

    const validateRequiredAtCreationFields = (): Array<ValidationError> => {
        const errors: Array<ValidationError> = [];
        const requiredTranslation = translateString("requiredField");

        for (const field of requiredAtCreationTemplateFields) {
            if (!hasFieldData(getLegalEntityFieldValue(field, editedLegalEntity, selectedTagIds))) {
                errors.push(new ValidationError(getFieldName(field), requiredTranslation));
            }
        }

        return errors;
    };

    const requiredFieldValidationErrors = showRequiredFieldErrors ? validateRequiredAtCreationFields() : [];

    const saveMutation = useDotLegalMutation(save);

    // The wizard opens on a brand new entity, so the steps must not flag missing required fields before the
    // user has had a chance to reach them.
    const navigateToCreatedLegalEntity = (legalEntityId: string) => {
        props.onDialogClose();
        navigate(getLegalEntityEditUrl(props.legalEntityScope!, legalEntityId, "create=true"));
    };

    const stayInFlow = () => {
        props.onDialogClose();
        snackbar.show(createElement(Trans, { i18nKey: "legalEntityAdded", values: { company: saveModel.name } }));
    };

    const redirectToFullCreation = (legalEntityId: string) => {
        props.onCreatedAndRedirect!(legalEntityId, toLegalEntityScope(saveModel.isCustomerOwned));
    };

    const saveThen = (afterEntityCreated: (legalEntityId: string) => void) => {
        const requiredFieldErrors = validateRequiredAtCreationFields();

        if (validation.anyHasErrors) {
            validation.setShowErrors(true);
        }

        if (requiredFieldErrors.length > 0) {
            setShowRequiredFieldErrors(true);
        }

        if (validation.anyHasErrors || requiredFieldErrors.length > 0) {
            return;
        }

        saveMutation.mutateAsync(undefined, {
            onSuccess: (response: any) => {
                const creationResult = response.value();

                if (!creationResult.isCreated) {
                    snackbar.show(translateString("addEntityDataExist"), "warning");
                    return;
                }

                if (creationResult.refetchJwtToken) {
                    setJwtToken("");
                }

                props.onEntityCreated?.(creationResult.id);
                afterEntityCreated(creationResult.id);
            },
        });
    };

    // Full create only makes sense when the caller can be returned to afterwards, and only for a user
    // allowed to manage the entity it lands on.
    const canCreateAndRedirect = canCreateCompany && props.onCreatedAndRedirect !== undefined;
    const okButtonTranslationKey = canCreateAndRedirect ? "quickCreate" : isInlineCreation ? "save" : "create";

    const onSaveClick = () => saveThen(isInlineCreation ? stayInFlow : navigateToCreatedLegalEntity);

    const onSaveAndRedirectClick = () => saveThen(redirectToFullCreation);

    function onTemplateChange(entityTemplateId: string | null) {
        setSaveModel({ ...saveModel, entityTemplateId: entityTemplateId ?? "" });
        setEditedLegalEntity({ ...createEmptyRequiredFieldValues(canCreateCompany), ...getCvrFieldValues(selectedCvrResult) });
        setSelectedTagIds([]);
        setShowRequiredFieldErrors(false);
    }

    async function onRequiredFieldChange(changeFunc: (legalEntity: LegalEntityFieldModel) => void) {
        setEditedLegalEntity((current) => {
            const next = { ...current };
            changeFunc(next);
            return next;
        });
    }

    const onSaveModelChange = {
        onNameChange: (name: string) => setSaveModel((current) => ({ ...current, name })),
        onCountryChange: (countryCode: string) => setSaveModel((current) => ({ ...current, countryCode })),
    };

    function onCvrLegalEntitySelected(legalEntity: CvrResultViewModel) {
        setSaveModel((current) => ({ ...current, name: legalEntity.name, countryCode: "DK" }));
        setEditedLegalEntity((current) => ({ ...current, ...getCvrFieldValues(legalEntity) }));
        setSelectedCvrResult(legalEntity);
    }

    function onCvrLegalEntityRemoved() {
        // Clearing the company must not undo the template choice or the required field values entered under it.
        setSaveModel({ ...getDefaultSaveModel(), entityTemplateId: saveModel.entityTemplateId });
        setEditedLegalEntity((current) => ({ ...current, ...getCvrFieldValues(undefined) }));
        setSelectedCvrResult(undefined);
    }

    return {
        saveModel,
        onSaveClick,
        onSaveAndRedirectClick,
        canCreateAndRedirect,
        okButtonTranslationKey,
        isSaving: saveMutation.isLoading,
        validation,
        onSaveModelChange,
        onCvrLegalEntitySelected,
        onCvrLegalEntityRemoved,
        selectedCvrResult,
        templates,
        templatesIsLoading,
        onTemplateChange,
        selectedTemplateMissingAccess,
        hasStandardRequiredFields,
        standardRequiredFields,
        customRequiredFields,
        tagsRequiredAtCreation,
        subContractorsRequiredAtCreation,
        selectedTagIds,
        setSelectedTagIds,
        editedLegalEntity,
        disableFields,
        onRequiredFieldChange,
        requiredFieldValidationErrors,
    };

    // The external endpoint is the one a role without create rights on the entity being created may reach, and
    // it is what pins the entity to unapproved. Creating with those rights is still an ordinary creation.
    function save() {
        return post<{}>(canCreateCompany ? "/legal-entities" : "/legal-entities/external", mapToSaveModel());
    }

    function mapToSaveModel() {
        return {
            ...saveModel,
            type: saveModel.type ?? (isExternalCreation ? getStandardTypeId(StandardLegalEntityType.Vendor) : undefined),
            entityTemplateId: saveModel.entityTemplateId || null,
            customFieldValues: editedLegalEntity.customFieldValues,
            note: editedLegalEntity.note,
            referenceNumber: editedLegalEntity.referenceNumber,
            description: editedLegalEntity.description,
            address: editedLegalEntity.address,
            zipCode: editedLegalEntity.zipCode,
            city: editedLegalEntity.city,
            companyRegistrationNumber: editedLegalEntity.companyRegistrationNumber,
            website: editedLegalEntity.website,
            types: editedLegalEntity.types,
            isApproved: editedLegalEntity.isApproved,
            isActive: editedLegalEntity.isActive,
            responsibleId: editedLegalEntity.responsibleId,
            businessAreaIds: editedLegalEntity.businessAreaIds,
            complianceAreaIds: editedLegalEntity.complianceAreaIds,
            certificationIds: editedLegalEntity.certificationIds,
            auditType: editedLegalEntity.auditType,
            auditFrequency: editedLegalEntity.auditFrequency,
            tagIds: selectedTagIds,
            subContractorIds: editedLegalEntity.subContractorIds,
        };
    }

    function getDefaultSaveModel(): LegalEntitySaveModel {
        return {
            name: "",
            countryCode: "",
            isCustomerOwned: isGroupEntityCreation,
            // Creating from the external list seeds the vendor type, which is what the overwhelming majority of
            // external entities are. The types are master data, so the id is not known until they have loaded
            // and the seed is resolved when the save payload is built. Inline creation seeds nothing, and a
            // group entity is defined by ownership.
            type: undefined,
            entityTemplateId: "",
        };
    }
}
